// =====================================================================
// Operator Dashboard / HMI — project-scoped saved widget boards.
// Spec: docs/superpowers/specs/2026-06-23-operator-dashboard-design.md
// =====================================================================
// A dashboard is a saved layout of widgets, each bound to a tag (read) or
// a relay/command (write), rendering live data from the live-tags stream.
// All optional config fields are nullable-with-default to stay tolerant of
// legacy docs (the board_catalog nullable!=optional lesson).
// =====================================================================

import { z } from 'zod';

/** Grid is GRID_COLS columns wide; widget x/w are in column units. */
export const GRID_COLS = 12;

export const DashboardWidgetKindSchema = z.enum([
  'value',
  'gauge',
  'trend',
  'status_tile',
  'relay_toggle',
]);
export type ParsedDashboardWidgetKind = z.infer<typeof DashboardWidgetKindSchema>;

export const DashboardWidgetSchema = z.object({
  id: z.string().min(1),
  kind: DashboardWidgetKindSchema,
  title: z.string().default(''),
  /** Doc-id of the bound tag (read widgets). null for unbound/relay widgets. */
  tag_id: z.string().nullable().default(null),
  // layout (grid units)
  x: z.number().int().nonnegative().default(0),
  y: z.number().int().nonnegative().default(0),
  w: z.number().int().positive().max(GRID_COLS).default(3),
  h: z.number().int().positive().default(2),
  // per-kind config (all optional, defaulted)
  min: z.number().nullable().default(null), // gauge lower bound
  max: z.number().nullable().default(null), // gauge upper bound
  units: z.string().nullable().default(null), // value/gauge suffix
  decimals: z.number().int().min(0).max(6).default(2),
  threshold: z.number().nullable().default(null), // status_tile: value >= threshold => "on"
  relay_pin: z.number().int().nullable().default(null), // relay_toggle
  relay_timeout_ms: z.number().int().nullable().default(null),
});
export type ParsedDashboardWidget = z.infer<typeof DashboardWidgetSchema>;

export const DashboardSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable().default(null),
  widgets: z.array(DashboardWidgetSchema).default([]),
  archived_at_ms: z.number().int().nullable().default(null),
  created_at_ms: z.number().int().nonnegative(),
  updated_at_ms: z.number().int().nonnegative(),
});
export type ParsedDashboard = z.infer<typeof DashboardSchema>;

// Create accepts name (required) + optional initial description/widgets.
// Widgets here may omit ids/layout; the server/UI mint+default them.
export const CreateDashboardWidgetSchema = DashboardWidgetSchema.partial({
  id: true,
}).extend({ kind: DashboardWidgetKindSchema });

export const CreateDashboardSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  widgets: z.array(CreateDashboardWidgetSchema).optional(),
});
export type ParsedCreateDashboard = z.infer<typeof CreateDashboardSchema>;

export const UpdateDashboardSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  widgets: z.array(CreateDashboardWidgetSchema).optional(),
});
export type ParsedUpdateDashboard = z.infer<typeof UpdateDashboardSchema>;
